'use client';

import {
  forwardRef,
  useCallback,
  useMemo,
  type ComponentPropsWithoutRef,
  type ReactNode,
  type Ref,
} from 'react';
import { automationId } from '@terra/shared-utils';
import { Box } from '@terra/ui-styled-system';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { Scroll } from '../Scroll';

type VirtualizedStackProps<TItem> = {
  data: readonly TItem[];
  renderItem: (params: { item: TItem; index: number }) => ReactNode;
  computeItemKey?: (params: { item: TItem; index: number }) => string;
  endReached?: (index: number) => void;
  onScrollerScroll?: () => void;
  footer?: ReactNode;
  overscan?: number;
  increaseViewportBy?: number;
  dataTestid?: string;
  virtuosoRef?: Ref<VirtuosoHandle>;
  scrollerRef?: (element: HTMLElement | Window | null) => void;
};

export function VirtualizedStack<TItem>({
  data,
  renderItem,
  computeItemKey,
  endReached,
  onScrollerScroll,
  footer,
  overscan = 400,
  increaseViewportBy = 600,
  dataTestid = '',
  virtuosoRef,
  scrollerRef,
}: VirtualizedStackProps<TItem>) {
  const itemContent = useCallback(
    (index: number, item: TItem) => renderItem({ item, index }),
    [renderItem],
  );

  const virtuosoComputeItemKey = useMemo(
    () =>
      computeItemKey
        ? (index: number, item: TItem) => computeItemKey({ item, index })
        : undefined,
    [computeItemKey],
  );

  const components = useMemo(() => {
    const Scroller = forwardRef<
      HTMLDivElement,
      ComponentPropsWithoutRef<'div'>
    >(({ style, children, onScroll, ...props }, ref) => (
      <Scroll
        ref={ref}
        style={style}
        h="full"
        w="full"
        showScrollbar
        onScroll={(event) => {
          onScroll?.(event);
          onScrollerScroll?.();
        }}
        {...props}
      >
        {children}
      </Scroll>
    ));

    Scroller.displayName = 'VirtualizedStackScroller';

    return {
      Scroller,
      List: VirtualizedStackList,
      Item: VirtualizedStackItem,
      EmptyPlaceholder: () => null,
      Footer: footer ? () => footer : undefined,
    };
  }, [footer, onScrollerScroll]);

  return (
    <Box
      h="full"
      w="full"
      display="flex"
      flexDirection="column"
      flex={1}
      overflow="hidden"
      minH="0"
      {...automationId(dataTestid)}
    >
      <Virtuoso
        ref={virtuosoRef}
        scrollerRef={scrollerRef}
        data={data}
        style={{ height: '100%', width: '100%' }}
        overscan={overscan}
        increaseViewportBy={increaseViewportBy}
        endReached={endReached}
        computeItemKey={virtuosoComputeItemKey}
        itemContent={itemContent}
        components={components}
      />
    </Box>
  );
}

const VirtualizedStackList = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(({ children, ...props }, ref) => (
  <Box ref={ref} w="full" {...props}>
    {children}
  </Box>
));

VirtualizedStackList.displayName = 'VirtualizedStackList';

const VirtualizedStackItem = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<'div'>
>(({ style, children, ...props }, ref) => (
  <Box
    ref={ref}
    w="full"
    minW="100%"
    alignSelf="stretch"
    display="block"
    style={{ ...style, width: '100%' }}
    {...props}
  >
    {children}
  </Box>
));

VirtualizedStackItem.displayName = 'VirtualizedStackItem';
